import { createContext } from "react";
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import {
  TypographyVariantsOptions,
  TypographyStyleOptions,
} from "@mui/material/styles/createTypography";


const THEME_PREF = "theme-preference";
const LIGHT = 0;
const DARK = 1;

export enum ThemeKind {
  Light = LIGHT,
  Dark = DARK,
}

const themes = {
  [ThemeKind.Light]: createLightTheme(),
  [ThemeKind.Dark]: createDarkTheme(),
};

function createDarkTheme() {
  const typography = createTypography();

  let theme = createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: "#EDB45F",
      },
      secondary: {
        main: "#6CB2E1",
      },
      error: {
        main: "#D44F64",
      },
      warning: {
        main: "#EDB45F",
      },
      info: {
        main: "#9E96C8",
      },
      success: {
        main: "#4E9C54",
      },
      background: {
        default: "#15181A",
        paper: "#15181A",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: { root: { textTransform: "none" } },
      },
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
      },
      MuiTooltip: {
        styleOverrides: { tooltipPlacementTop: { top: 7 } },
      },
      MuiDialog: { defaultProps: { PaperProps: { elevation: 2 } } },
    },
    typography,
  });

  theme = responsiveFontSizes(theme);

  return theme;
}

function createLightTheme() {
  const typography = createTypography();

  let theme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#2D7048",
      },
      secondary: {
        main: "#1482CC",
      },
      error: {
        main: "#D44F64",
      },
      warning: {
        main: "#E5A23E",
      },
      info: {
        main: "#7365B8",
      },
      success: {
        main: "#4E9C54",
      },
      background: {
        default: "#F2F2F2",
      },
    },
    components: {
      MuiAppBar: { defaultProps: { color: "inherit" } },
      MuiButton: {
        styleOverrides: { root: { textTransform: "none" } },
      },
      MuiButtonBase: { defaultProps: { disableRipple: true } },
      MuiTooltip: {
        styleOverrides: { tooltipPlacementTop: { top: 7 } },
      },
    },
    typography,
  });

  theme = responsiveFontSizes(theme);

  return theme;
}

function createTypography() {
  const header: TypographyStyleOptions = {
    fontFamily: '"Ubuntu", sans-serif',
  };

  const typography: TypographyVariantsOptions = {
    fontFamily: '"Open Sans", sans-serif',
    h1: header,
    h2: header,
    h3: header,
    h4: header,
    h5: header,
    h6: header,
  };

  return typography;
}

export function getTheme(kind: ThemeKind) {
  return themes[kind];
}

export function getThemeKind() {
  const pref = getThemePref();
  const kind = matchKind(pref);

  return kind;
}

function matchKind(input: string | null) {
  if (input == null) return ThemeKind.Light;

  const value = parseInt(input);
  if (isNaN(value)) return ThemeKind.Light;

  switch (value) {
    case LIGHT:
      return ThemeKind.Light;
    case DARK:
      return ThemeKind.Dark;
    default:
      return ThemeKind.Light;
  }
}

function getThemePref() {
  return localStorage.getItem(THEME_PREF);
}

export function setThemePref(theme: ThemeKind) {
  localStorage.setItem(THEME_PREF, theme.toString());
}

export const ThemeKindContext = createContext({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setThemeKind: (kind: ThemeKind) => { },
  kind: ThemeKind.Light,
});
